import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar,
  AlertCircle,
  Link2,
  GitFork,
  Plus,
  Trash2,
  Zap,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Clock,
  CheckCircle2,
  ArrowRight,
  ShieldAlert,
} from 'lucide-react';
import { Task, TaskDependency } from '../../../shared/types.ts';
import { Avatar } from '../common/Avatar.tsx';
import { useAuth } from '../../context/AuthContext.tsx';

interface TimelineViewProps {
  tasks: Task[];
  projectId?: string;
  onTaskClick: (task: Task) => void;
  onUpdateTaskDates?: (taskId: string, startDate: string, dueDate: string) => void;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  tasks,
  projectId,
  onTaskClick,
  onUpdateTaskDates,
}) => {
  const { token } = useAuth();
  const [dependencies, setDependencies] = useState<TaskDependency[]>([]);
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const [isAddDepOpen, setIsAddDepOpen] = useState(false);
  const [predTaskId, setPredTaskId] = useState('');
  const [succTaskId, setSuccTaskId] = useState('');
  const [windowOffset, setWindowOffset] = useState(0); // Offset in days from today
  const [zoomLevel, setZoomLevel] = useState<'days' | 'weeks'>('days');
  const [wheelMode, setWheelMode] = useState<'auto' | 'horizontal'>('auto');

  const containerRef = useRef<HTMLDivElement>(null);

  const fetchDependencies = async () => {
    if (!token || !projectId) return;
    try {
      const res = await fetch(`/api/features/dependencies/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setDependencies(d.dependencies || []);
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchDependencies();
  }, [token, projectId]);

  const handleAddDependency = async () => {
    if (!predTaskId || !succTaskId || predTaskId === succTaskId || !projectId) return;
    try {
      await fetch('/api/features/dependencies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ projectId, predecessorId: predTaskId, successorId: succTaskId }),
      });
      fetchDependencies();
      setIsAddDepOpen(false);
      setPredTaskId('');
      setSuccTaskId('');
    } catch (e) {}
  };

  const handleDeleteDependency = async (id: string) => {
    try {
      await fetch(`/api/features/dependencies/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setDependencies((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {}
  };

  // Generate date columns (45 days in standard mode, 60 days in extended mode so horizontal scroll is always rich)
  const today = new Date();
  const daysCount = zoomLevel === 'days' ? 45 : 60;
  const startDayOffset = -10 + windowOffset;
  const dayColWidth = zoomLevel === 'days' ? 54 : 42;
  const days: Date[] = [];
  for (let i = startDayOffset; i < startDayOffset + daysCount; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }

  // Auto-position horizontal scroll near Today on initial mount
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const todayIdx = days.findIndex((d) => d.toDateString() === today.toDateString());
    if (todayIdx > 2) {
      el.scrollLeft = Math.max(0, (todayIdx - 3) * dayColWidth);
    }
  }, [zoomLevel]);

  const scrollToToday = () => {
    setWindowOffset(0);
    requestAnimationFrame(() => {
      const el = containerRef.current;
      if (!el) return;
      el.scrollTo({ left: Math.max(0, 7 * dayColWidth), behavior: 'smooth' });
    });
  };

  // Handle both vertical and horizontal mouse-wheel scrolling smoothly
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;

    // If user is already scrolling horizontally with trackpad / tilt wheel, let browser handle it natively
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      return;
    }

    if (e.deltaY === 0) return;

    const target = e.target as HTMLElement;
    const isOverHeader = Boolean(target.closest('[data-gantt-header="true"]'));

    const canScrollHorizontallyRight =
      e.deltaY > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    const canScrollHorizontallyLeft = e.deltaY < 0 && el.scrollLeft > 1;
    const canScrollHorizontally = canScrollHorizontallyRight || canScrollHorizontallyLeft;

    // 1. If Shift is held, or user enabled Horizontal Wheel mode, or cursor is over the Days Header: scroll horizontally
    if (e.shiftKey || wheelMode === 'horizontal' || isOverHeader) {
      if (canScrollHorizontally) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
      return;
    }

    // Anywhere else the wheel only scrolls vertically. It must not chain into a sideways scroll
    // when the bottom/top is reached, so the timeline never jumps across dates on its own.
  };

  const formatDayHeader = (d: Date) => {
    return {
      name: d.toLocaleDateString([], { weekday: 'short' }),
      num: d.getDate(),
      month: d.toLocaleDateString([], { month: 'short' }),
      isToday: d.toDateString() === today.toDateString(),
      iso: d.toISOString().split('T')[0],
    };
  };

  // Compute Critical Path Network:
  // Identify root blockers, successors, and longest dependent chain
  const dependencyGraph = React.useMemo(() => {
    const predecessorMap: Record<string, string[]> = {};
    const successorMap: Record<string, string[]> = {};

    dependencies.forEach((dep) => {
      if (!predecessorMap[dep.successorId]) predecessorMap[dep.successorId] = [];
      predecessorMap[dep.successorId].push(dep.predecessorId);

      if (!successorMap[dep.predecessorId]) successorMap[dep.predecessorId] = [];
      successorMap[dep.predecessorId].push(dep.successorId);
    });

    // Critical Path nodes: Tasks that are linked in dependencies and carry urgent/high priority or direct blocking status
    const criticalTaskIds = new Set<string>();

    dependencies.forEach((dep) => {
      criticalTaskIds.add(dep.predecessorId);
      criticalTaskIds.add(dep.successorId);
    });

    tasks.forEach((t) => {
      if (t.priority === 'urgent' && (successorMap[t.id] || predecessorMap[t.id])) {
        criticalTaskIds.add(t.id);
      }
    });

    return { predecessorMap, successorMap, criticalTaskIds };
  }, [dependencies, tasks]);

  // Tasks with date or fallback schedule
  const scheduledTasks = tasks.map((t, idx) => {
    let startIso = t.startDate;
    let dueIso = t.dueDate;

    if (!dueIso && !startIso) {
      // Default placeholder timeline window so every task is trackable in Gantt
      const d = new Date(today);
      d.setDate(today.getDate() + (idx % 5));
      startIso = d.toISOString().split('T')[0];
      const due = new Date(d);
      due.setDate(d.getDate() + 3);
      dueIso = due.toISOString().split('T')[0];
    } else if (!startIso && dueIso) {
      const d = new Date(dueIso);
      d.setDate(d.getDate() - 2);
      startIso = d.toISOString().split('T')[0];
    } else if (startIso && !dueIso) {
      const d = new Date(startIso);
      d.setDate(d.getDate() + 2);
      dueIso = d.toISOString().split('T')[0];
    }

    return {
      ...t,
      computedStart: startIso!,
      computedDue: dueIso!,
    };
  });

  const getPriorityStyle = (priority: string, isCritical: boolean) => {
    if (isCritical && showCriticalPath) {
      return 'bg-gradient-to-r from-rose-600 to-amber-500 text-white ring-2 ring-rose-400 font-bold shadow-md animate-pulse-slow';
    }
    switch (priority) {
      case 'urgent':
        return 'bg-rose-500 text-white';
      case 'high':
        return 'bg-amber-500 text-white';
      case 'medium':
        return 'bg-[#121417] text-white dark:bg-white dark:text-black';
      default:
        return 'bg-surface-400 text-white dark:bg-surface-700';
    }
  };

  const totalMinWidth = 260 + days.length * dayColWidth;

  return (
    <div className="bg-white dark:bg-surface-900 rounded-2xl sm:rounded-3xl shadow-neo-card border border-[#d5d8de]/60 dark:border-surface-800 overflow-hidden font-sans">
      {/* Gantt Header & Critical Path Mapping Controls */}
      <div className="p-3 sm:p-4 border-b border-surface-200 dark:border-surface-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center space-x-2 text-xs font-black text-surface-900 dark:text-surface-100">
            <Calendar className="w-4 h-4 text-brand-600" />
            <span className="uppercase tracking-wider">Gantt & Critical Path</span>
          </div>

          {/* Critical Path Toggle */}
          <button
            onClick={() => setShowCriticalPath(!showCriticalPath)}
            className={`px-3 py-1.5 min-h-[34px] rounded-xl text-xs font-extrabold flex items-center space-x-1.5 transition-all shadow-sm ${
              showCriticalPath
                ? 'bg-rose-600 text-white ring-2 ring-rose-300 dark:ring-rose-900'
                : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:bg-surface-200'
            }`}
            title="Highlight network bottleneck chain"
          >
            <Zap className="w-3.5 h-3.5 fill-current" />
            <span>Critical Path ({dependencyGraph.criticalTaskIds.size})</span>
          </button>

          {/* Dependency Link Button */}
          {projectId && (
            <button
              onClick={() => setIsAddDepOpen(!isAddDepOpen)}
              className="px-3 py-1.5 min-h-[34px] rounded-xl text-xs font-bold bg-pulse-gradient text-white hover:opacity-95 shadow-pulse transition-all flex items-center space-x-1.5"
            >
              <GitFork className="w-3.5 h-3.5 text-white/90" />
              <span>Link Dep ({dependencies.length})</span>
            </button>
          )}
        </div>

        {/* Date Navigation, Mouse Wheel Mode & View Window */}
        <div className="flex flex-wrap items-center gap-1.5 text-xs self-start sm:self-auto">
          {/* Mouse Wheel Mode Toggle */}
          <button
            type="button"
            onClick={() => setWheelMode((prev) => (prev === 'auto' ? 'horizontal' : 'auto'))}
            className={`px-2.5 py-1.5 min-h-[34px] rounded-lg border text-[11px] font-bold transition-colors ${
              wheelMode === 'horizontal'
                ? 'bg-brand-600 text-white border-brand-600'
                : 'border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
            }`}
            title="Switch mouse wheel behavior between Auto (Vertical + Horizontal) and Always Horizontal"
          >
            {wheelMode === 'horizontal' ? '↔ Wheel: Horizontal' : '↕↔ Wheel: Auto'}
          </button>

          {/* Zoom Level Toggle */}
          <button
            type="button"
            onClick={() => setZoomLevel((prev) => (prev === 'days' ? 'weeks' : 'days'))}
            className="px-2.5 py-1.5 min-h-[34px] rounded-lg border border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 text-[11px] font-bold flex items-center space-x-1"
            title="Toggle 45-day vs 60-day timeline density"
          >
            {zoomLevel === 'days' ? (
              <>
                <Minimize2 className="w-3.5 h-3.5" />
                <span>60d View</span>
              </>
            ) : (
              <>
                <Maximize2 className="w-3.5 h-3.5" />
                <span>45d View</span>
              </>
            )}
          </button>

          <button
            onClick={() => {
              if (containerRef.current && containerRef.current.scrollLeft > 200) {
                containerRef.current.scrollBy({ left: -350, behavior: 'smooth' });
              } else {
                setWindowOffset((prev) => prev - 7);
              }
            }}
            className="p-1.5 min-h-[34px] min-w-[34px] flex items-center justify-center rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800"
            title="Scroll Left / Previous 7 days"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={scrollToToday}
            className="px-3 py-1.5 min-h-[34px] rounded-lg border border-surface-200 dark:border-surface-700 font-bold hover:bg-surface-100 dark:hover:bg-surface-800 text-[11px]"
          >
            Today
          </button>
          <button
            onClick={() => {
              const el = containerRef.current;
              if (el && el.scrollLeft + el.clientWidth < el.scrollWidth - 200) {
                el.scrollBy({ left: 350, behavior: 'smooth' });
              } else {
                setWindowOffset((prev) => prev + 7);
              }
            }}
            className="p-1.5 min-h-[34px] min-w-[34px] flex items-center justify-center rounded-lg border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800"
            title="Scroll Right / Next 7 days"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Dependency Link Creator Panel */}
      {isAddDepOpen && (
        <div className="p-3 sm:p-4 bg-surface-50 dark:bg-surface-800/80 border-b border-surface-200 dark:border-surface-700 flex flex-col sm:flex-row sm:items-center flex-wrap gap-2.5 sm:gap-3 text-xs">
          <div className="flex items-center space-x-1.5">
            <span className="font-extrabold text-surface-800 dark:text-surface-200">Predecessor (Blocks):</span>
            <select
              value={predTaskId}
              onChange={(e) => setPredTaskId(e.target.value)}
              className="p-1.5 rounded-xl border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-900 text-surface-800 dark:text-surface-200 text-xs font-medium"
            >
              <option value="">Select prerequisite task...</option>
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>

          <ArrowRight className="w-4 h-4 text-surface-400" />

          <div className="flex items-center space-x-1.5">
            <span className="font-extrabold text-surface-800 dark:text-surface-200">Successor (Blocked):</span>
            <select
              value={succTaskId}
              onChange={(e) => setSuccTaskId(e.target.value)}
              className="p-1.5 rounded-xl border border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-900 text-surface-800 dark:text-surface-200 text-xs font-medium"
            >
              <option value="">Select dependent task...</option>
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleAddDependency}
            className="px-4 py-1.5 bg-pulse-gradient text-white rounded-xl font-bold shadow-pulse hover:opacity-95 transition-all"
          >
            Create Gantt Link
          </button>

          {dependencies.length > 0 && (
            <div className="w-full flex flex-wrap gap-2 pt-2 border-t border-surface-200 dark:border-surface-700">
              <span className="text-[10px] uppercase font-bold text-surface-400">Existing Links:</span>
              {dependencies.map((dep) => {
                const pred = tasks.find((t) => t.id === dep.predecessorId);
                const succ = tasks.find((t) => t.id === dep.successorId);
                return (
                  <span
                    key={dep.id}
                    className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 text-[11px]"
                  >
                    <span className="font-semibold text-rose-600 dark:text-rose-400 truncate max-w-[120px]">
                      {pred?.title || 'Prereq'}
                    </span>
                    <span>→</span>
                    <span className="font-semibold text-brand-600 dark:text-brand-400 truncate max-w-[120px]">
                      {succ?.title || 'Dependent'}
                    </span>
                    <button
                      onClick={() => handleDeleteDependency(dep.id)}
                      className="ml-1 text-surface-400 hover:text-rose-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Main Gantt Timeline Viewport (Supports both vertical and horizontal mouse-wheel scrolling) */}
      <div
        className="overflow-auto max-h-[calc(100vh-14.5rem)] touch-scroll relative"
        ref={containerRef}
        onWheel={handleWheel}
      >
        <div style={{ minWidth: `${totalMinWidth}px` }}>
          {/* Days Grid Header (Sticky Top) */}
          <div
            data-gantt-header="true"
            className="grid border-b border-surface-200 dark:border-surface-800 bg-[#f8f9fa] dark:bg-surface-800 text-center sticky top-0 z-20 shadow-2xs"
            style={{
              gridTemplateColumns: `260px repeat(${days.length}, minmax(${dayColWidth}px, 1fr))`,
            }}
          >
            <div
              data-gantt-left-col="true"
              className="py-2.5 px-4 text-left text-[11px] font-black uppercase tracking-wider text-surface-500 border-r border-surface-200 dark:border-surface-800 sticky left-0 z-30 bg-[#f8f9fa] dark:bg-surface-800"
            >
              Task Breakdown ({scheduledTasks.length})
            </div>

            {days.map((d, idx) => {
              const info = formatDayHeader(d);
              const showMonthBadge = idx === 0 || info.num === 1;
              return (
                <div
                  key={idx}
                  className={`py-1.5 px-1 border-r border-surface-200/60 dark:border-surface-800/60 text-xs ${
                    info.isToday
                      ? 'bg-brand-500/15 text-brand-600 dark:text-brand-400 font-black'
                      : 'text-surface-500'
                  }`}
                >
                  <div className="text-[9px] uppercase font-bold truncate">
                    {showMonthBadge ? `${info.month} ${info.name}` : info.name}
                  </div>
                  <div
                    className={`text-xs mt-0.5 inline-block w-6 h-6 leading-6 rounded-full font-bold ${
                      info.isToday ? 'bg-pulse-gradient text-white shadow-pulse' : ''
                    }`}
                  >
                    {info.num}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Gantt Task Rows & Dependency Visualizer */}
          <div className="divide-y divide-surface-100 dark:divide-surface-800/60">
            {scheduledTasks.map((task) => {
              const isPredecessor = !!dependencyGraph.successorMap[task.id];
              const isSuccessor = !!dependencyGraph.predecessorMap[task.id];
              const isCritical = dependencyGraph.criticalTaskIds.has(task.id);

              const firstDayIso = days[0].toISOString().split('T')[0];
              const lastDayIso = days[days.length - 1].toISOString().split('T')[0];

              // Calculate start and end indices in current visible window
              let startIdx = days.findIndex((d) => d.toISOString().split('T')[0] === task.computedStart);
              let endIdx = days.findIndex((d) => d.toISOString().split('T')[0] === task.computedDue);

              if (startIdx === -1 && endIdx === -1) {
                if (task.computedDue < firstDayIso) {
                  startIdx = 0;
                  endIdx = 0;
                } else if (task.computedStart > lastDayIso) {
                  startIdx = days.length - 1;
                  endIdx = days.length - 1;
                } else {
                  startIdx = 0;
                  endIdx = days.length - 1;
                }
              } else {
                if (startIdx === -1) startIdx = 0;
                if (endIdx === -1) endIdx = days.length - 1;
              }

              if (endIdx < startIdx) {
                endIdx = startIdx;
              }

              const colSpan = Math.max(1, endIdx - startIdx + 1);

              return (
                <div
                  key={task.id}
                  className="grid hover:bg-surface-50 dark:hover:bg-surface-800/30 transition-colors py-2 items-center relative group"
                  style={{
                    gridTemplateColumns: `260px repeat(${days.length}, minmax(${dayColWidth}px, 1fr))`,
                  }}
                >
                  {/* Left Column (Sticky Left): Task Name, Critical Path & Dependency tags */}
                  <div
                    data-gantt-left-col="true"
                    className="px-4 pr-3 truncate flex items-center justify-between border-r border-surface-200 dark:border-surface-800 sticky left-0 z-10 bg-white dark:bg-surface-900 group-hover:bg-surface-50 dark:group-hover:bg-surface-800/90 py-1"
                  >
                    <div className="truncate">
                      <button
                        onClick={() => onTaskClick(task)}
                        className="text-xs font-bold text-surface-900 dark:text-surface-100 hover:text-brand-600 truncate text-left block w-full leading-tight"
                      >
                        {task.title}
                      </button>
                      <div className="flex items-center space-x-1.5 text-[10px] text-surface-400 mt-0.5">
                        <span>{task.computedDue}</span>
                        {isPredecessor && (
                          <span className="px-1 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 font-bold">
                            Prereq
                          </span>
                        )}
                        {isSuccessor && (
                          <span className="px-1 rounded bg-rose-500/20 text-rose-600 dark:text-rose-400 font-bold">
                            Blocked
                          </span>
                        )}
                      </div>
                    </div>

                    <Avatar
                      src={task.assigneeAvatar}
                      name={task.assigneeName || 'U'}
                      size="xs"
                      className="shrink-0 ml-2"
                    />
                  </div>

                  {/* Gantt Timeline Bar Span */}
                  <div
                    className="relative h-8 flex items-center"
                    style={{
                      gridColumnStart: startIdx + 2,
                      gridColumnEnd: `span ${colSpan}`,
                    }}
                  >
                    <div
                      onClick={() => onTaskClick(task)}
                      className={`w-full h-7 rounded-xl px-2.5 flex items-center justify-between text-xs font-bold cursor-pointer shadow-sm hover:scale-[1.02] transition-transform truncate z-10 ${getPriorityStyle(
                        task.priority,
                        isCritical
                      )}`}
                    >
                      <div className="flex items-center space-x-1.5 truncate pr-1">
                        {isPredecessor && (
                          <GitFork className="w-3 h-3 text-brand-300 stroke-[2.5] shrink-0" />
                        )}
                        <span className="truncate">{task.title}</span>
                      </div>
                      <span className="text-[10px] opacity-80 shrink-0 font-mono">
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
